import { KebabButton } from '@src/components/common/contextMenuStyles';
import { GeneratingNameLabel } from '@src/components/common/GeneratingNameLabel';
import { InlineRenameField } from '@src/components/common/InlineRenameField';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import DragHandleIcon from '@src/svgs/DragHandleIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import React, { useState } from 'react';
import styled from 'styled-components';

import type { ReelChapter } from '@src/services/reel/reelTypes';

interface ReelChapterRowProps {
  chapter: ReelChapter;
  /** 1-based position, which is also this chapter's title fallback (DS5). */
  ordinal: number;
  /** How many Sections run under it, down to the next boundary. */
  sectionCount: number;
  /**
   * What those Sections add up to — voices, count and running time, formatted by
   * the panel with the same helper as the reel's own summary, so a chapter reads
   * as that line scoped to its Sections rather than a second way of saying it.
   * Unused while the chapter is empty, which says what it is waiting for instead.
   */
  summary: string;
  /** Commits a typed title; the empty string clears back to the ordinal. */
  onRename: (chapterId: string, title: string) => void;
  /**
   * The Chapter menu's Edit Name, landing on the field the title already has:
   * the panel owns which heading was asked (one menu serves the whole list), and
   * the heading opens its own pencil rather than growing a second way to type a
   * title.
   */
  isRenameRequested?: boolean;
  onRenameRequestHandled?: () => void;
  /**
   * PLAT-11075 — the heading is a drop target for this chapter's start, and an
   * empty chapter grows a strip to drop into while a *Section* drag is in
   * flight: with no rows of its own there is otherwise nothing to aim at.
   * Nothing about the resting list changes, since the strip is only there
   * during a drag.
   */
  isDragActive?: boolean;
  isDropTarget?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  /**
   * PLAT-11056 — folded away, and the gesture that folds it. Collapsing is the
   * creator's, so it is stored on the chapter and comes back with it (DS3).
   */
  collapsed?: boolean;
  onToggleCollapse?: (chapterId: string, collapsed: boolean) => void;
  /**
   * PLAT-11064 — where a right-click landed on this heading, for the panel's
   * single Chapter menu. The heading reports the point and keeps no state of its
   * own — the same division `ReelSectionRow` takes, which is what lets one
   * portaled menu serve a scrolling list.
   */
  onContextMenu?: (chapterId: string, x: number, y: number) => void;
  /** The Chapter menu is open on this heading, so it holds its hover fill. */
  hasOpenMenu?: boolean;
  /**
   * PLAT-11064 — a title is being generated for this Chapter (DS1), or the last
   * generation failed (DS4). Shown on the heading, which is where the result
   * lands; the menu closes before it acts, so there is nothing there to spin.
   */
  isGeneratingName?: boolean;
  nameError?: string;
  /**
   * PLAT-11064 DS2 — the Sections under this Chapter have changed since its
   * title was generated, so the title is stale. A hint, never an automatic
   * re-generation: the creator may be mid-restructure.
   */
  isStale?: boolean;
  /** This chapter is the one being dragged, or the one a drop would land above. */
  isDragging?: boolean;
  isChapterDropTarget?: boolean;
  onDragStart?: (e: React.DragEvent, chapterId: string) => void;
  onDragEnd?: () => void;
}

/**
 * PLAT-11055 — a chapter heading over the Sections it owns.
 *
 * An unnamed chapter shows its ordinal, derived rather than stored, so
 * inserting a chapter above it renumbers it instead of leaving stale text
 * behind (DS5). That is PLAT-11054's stored-name / display-name split reused,
 * which is why `InlineRenameField` serves both without knowing about either.
 *
 * PLAT-11056 — chevron folds, grip moves: the same handle column a Section row
 * carries, one control per job. The *heading* carries `draggable`, not the grip —
 * HTML5 DnD drags whichever element has the attribute, so a draggable grip would
 * drag only itself and leave the chapter behind. A chapter that owns nothing
 * shows neither: there is nothing to fold, and its position comes from its anchor
 * rather than the timeline, so there is nothing to drag (DS7).
 */
export const ReelChapterRow: React.FC<ReelChapterRowProps> = ({
  chapter,
  ordinal,
  sectionCount,
  summary,
  onRename,
  isRenameRequested = false,
  onRenameRequestHandled,
  isDragActive = false,
  isDropTarget = false,
  onDragOver,
  onDrop,
  collapsed = false,
  onToggleCollapse,
  isDragging = false,
  isChapterDropTarget = false,
  onContextMenu,
  hasOpenMenu = false,
  isGeneratingName = false,
  nameError,
  isStale = false,
  onDragStart,
  onDragEnd,
}) => {
  // A draggable ancestor wins over text selection inside an input, so the
  // heading stops being draggable while its title is open for editing.
  const [isRenaming, setIsRenaming] = useState(false);
  const isEmpty = sectionCount === 0;

  return (
    <>
      <Header
        data-testid={`reel-chapter-row-${ordinal}`}
        draggable={!!onDragStart && !isRenaming}
        $isDraggable={!!onDragStart}
        $isDragging={isDragging}
        $hasOpenMenu={hasOpenMenu}
        // Above for a chapter arriving over this one; below for a Section
        // appended to this one while it is folded — with the rows unmounted,
        // the end of the chapter is the line under its heading (DS4).
        $dropEdge={
          isChapterDropTarget
            ? 'above'
            : collapsed && isDropTarget
              ? 'below'
              : undefined
        }
        onContextMenu={e => {
          if (!onContextMenu) return;
          // Text being edited keeps the browser's own menu — cut, copy, paste,
          // spellcheck — because that is the only way to paste a title in
          // (PLAT-11060 DS6's reasoning, one level up).
          if ((e.target as HTMLElement).closest('input, textarea')) return;
          e.preventDefault();
          // The context-menu key and Shift+F10 fire `contextmenu` natively with
          // no useful pointer position, so the menu opens on the heading itself
          // rather than in the corner of the window.
          const hasPointer = e.clientX !== 0 || e.clientY !== 0;
          const rect = e.currentTarget.getBoundingClientRect();
          onContextMenu(
            chapter.id,
            hasPointer ? e.clientX : rect.left,
            hasPointer ? e.clientY : rect.bottom,
          );
        }}
        onDragStart={e => onDragStart?.(e, chapter.id)}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <HandleColumn>
          {/* The grip, where the chevron used to sit above it. `aria-hidden`,
              like the row's: it is the affordance, and the heading is what
              actually drags. Absent while a search filters the list, where a
              position means nothing — the panel withholds `onDragStart`
              (PLAT-11074 DS5). */}
          {onDragStart && (
            <Handle
              aria-hidden="true"
              data-testid={`reel-chapter-handle-${ordinal}`}
            >
              <DragHandleIcon />
            </Handle>
          )}
        </HandleColumn>
        <Lines>
          <TitleLine>
            <Title $isEditing={isRenaming || isRenameRequested}>
              <InlineRenameField
                // Remounted when the menu asks for the field, which is what
                // opens it: `autoEdit` is an initial state, so the flag alone
                // would do nothing on a field that is already resting here.
                key={isRenameRequested ? 'editing' : 'resting'}
                autoEdit={isRenameRequested}
                value={chapter.title}
                placeholder={`Chapter ${ordinal}`}
                ariaLabel={`Rename chapter ${ordinal}`}
                testId={`reel-chapter-name-${ordinal}`}
                // No pencil or wash on hover (PLAT-11397): the kebab's Edit
                // Name is the way in, and keyboard focus still shows it.
                hoverReveal={false}
                // A heading over its Sections, not content of its own:
                // semi-bold so the chapter reads as structure, and a step down
                // from the Section names so it does not compete with them. Its
                // count keeps `--text-muted` — that is metadata, and a step
                // further back again.
                heading
                onEditingChange={editing => {
                  setIsRenaming(editing);
                  // Committed or cancelled, so the request is spent — else the
                  // key would hold the field open and reopen it on every
                  // render.
                  if (!editing) onRenameRequestHandled?.();
                }}
                onCommit={title => onRename(chapter.id, title)}
              />
            </Title>
            {/* PLAT-11064 DS2 — the title no longer describes what is under it.
                A hint only: the action it points at is in the right-click menu,
                because re-titling under a creator who is still moving Sections
                around is the failure this avoids. */}
            {isStale && !isGeneratingName && (
              <StaleHint
                data-testid={`reel-chapter-stale-${ordinal}`}
                title="The clips in this chapter have changed since its name was generated"
              >
                Contents changed
              </StaleHint>
            )}
          </TitleLine>
          <Count
            data-testid={`reel-chapter-count-${ordinal}`}
            $isError={!!nameError}
            title={nameError}
            // Only while it is standing in for the summary: announced then, the
            // same as the Section row's two status lines. On the summary itself
            // it would announce every count change for nothing.
            role={nameError || isGeneratingName ? 'status' : undefined}
          >
            {/* An empty chapter is a legitimate holder, not a broken one — say
                what it is waiting for rather than printing "0 sections" (DS7),
                and no running time either: "00:00" would read as broken. A
                folded chapter keeps saying it, which is the whole of what it
                shows while its rows are unmounted (PLAT-11056). */}
            {/* One line, and the most urgent thing on it: a failure, else the
                generation in flight, else what the chapter holds. */}
            {nameError ??
              (isGeneratingName ? <GeneratingNameLabel /> : undefined) ??
              (isEmpty ? 'No clips yet' : summary)}
          </Count>
        </Lines>
        {/* The Section rows' rail, at chapter scale: what to do with this
            chapter above, whether to fold it below. Both on the right, where
            the card's own controls live, rather than a chevron in the handle
            column and a delete that only appears on a hover. */}
        <RightRail>
          {onContextMenu && (
            <ChapterMenuButton
              type="button"
              data-chapter-menu
              aria-label={`Chapter ${ordinal} actions`}
              aria-haspopup="menu"
              aria-expanded={hasOpenMenu || undefined}
              data-testid={`reel-chapter-menu-button-${ordinal}`}
              onClick={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                onContextMenu(chapter.id, rect.left, rect.bottom + 2);
              }}
            >
              <MoreVertIcon width="14" height="14" />
            </ChapterMenuButton>
          )}
          {/* Nothing to fold on a chapter that owns nothing, so it gets no
              chevron — but it keeps the grip, because its place is still its
              own to change (PLAT-11072). */}
          {!isEmpty && (
            <Disclosure
              type="button"
              aria-expanded={!collapsed}
              aria-label={`${collapsed ? 'Expand' : 'Collapse'} chapter ${ordinal}`}
              data-testid={`reel-chapter-disclosure-${ordinal}`}
              $collapsed={collapsed}
              onClick={() => onToggleCollapse?.(chapter.id, !collapsed)}
            >
              {/* The weight the transcript tab's heading chevrons wear, so the
                  same control reads the same on both tabs. */}
              <ChevronDownIcon strokeWidth="2.5" />
            </Disclosure>
          )}
        </RightRail>
      </Header>
      {/* Only while a Section is being dragged: an empty chapter is a heading and
          nothing else at rest, and a permanent strip under it would read as a
          broken row rather than an invitation (PLAT-11075 DS3). */}
      {isEmpty && isDragActive && (
        <DropZone
          data-testid={`reel-chapter-dropzone-${ordinal}`}
          $isDropTarget={isDropTarget}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          Drop a clip here
        </DropZone>
      )}
    </>
  );
};

/** The body an empty chapter does not otherwise have, so a drag has something
    to aim at. Dashed rather than filled: it is a space to put something in. */
const DropZone = styled.div<{ $isDropTarget?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  margin: 0 10px 4px;
  border: 1px dashed
    ${props => (props.$isDropTarget ? 'var(--primary)' : 'var(--border)')};
  border-radius: var(--radius-input);
  background-color: ${props =>
    props.$isDropTarget ? 'var(--surface-elevated)' : 'transparent'};
  color: ${props =>
    props.$isDropTarget ? 'var(--primary)' : 'var(--text-muted)'};
  font-size: var(--text-xs);
  transition:
    border-color var(--transition-fast),
    background-color var(--transition-fast),
    color var(--transition-fast);
`;

const Header = styled.div<{
  $isDraggable?: boolean;
  $isDragging?: boolean;
  $dropEdge?: 'above' | 'below';
  $hasOpenMenu?: boolean;
}>`
  /* Sticky, so the chapter a creator is reading inside names itself the whole
     way down it — the transcript tab's own behaviour, brought over so a chapter
     reads as the same object on both surfaces. The list is the scroller and
     every heading is a flat child of it, so each one rides the top until the
     next arrives and takes the place over.

     Over the Section cards passing under it: they paint later in document order,
     so without a stacking order of its own the heading would end up behind them.
     Under the portaled menus, which sit on the modal's own z-scale. */
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  /* The Section row's own gap, so the handle column lands on its handle column
     and the lines beside it start on its thumbnail edge. */
  gap: 10px;
  /* The Section rows' own box: they run the full width of the list and inset
     their content by 10px, so the heading does the same rather than sitting
     inside them — a card narrower than the cards it heads reads as filed under
     one of them.

     The space above it is the transcript tab's own, and it is what gives the
     boundary rule closing the chapter above room to read as that chapter's
     end rather than as this one's lid. Free while the heading is stuck: sticky
     pins the border box at the offset, not the margin box, so the heading
     still rides flush against the top of the list. */
  margin: 18px 0 4px;
  padding: 6px 10px;
  border-radius: var(--radius-input);
  /* The elevated grey the transcript tab's chapter heading wears, so a chapter
     reads as the same card on both surfaces. Opaque, so while this is stuck the
     rows running full-bleed underneath it pass under the band rather than
     through it. */
  background: var(--surface-elevated);
  /* The fill carried 4px further down in page background: that is the one gap
     the card cannot reach itself, and while the heading is stuck a row would
     otherwise slide through it. Downward only, deliberately — the ring this
     replaced painted 6px UP as well, over the boundary rule closing the chapter
     above, clipping its Insert chip and the reel time beside it. Nothing above
     the heading ever needs covering: while it is stuck its top edge is the top
     of the list. */
  box-shadow: 0 4px 0 0 var(--background);
  opacity: ${props => (props.$isDragging ? 0.4 : 1)};
  ${props => props.$isDraggable && 'cursor: grab;'}

  &:active {
    ${props => props.$isDraggable && 'cursor: grabbing;'}
  }

  /* No rename pencil on hover (PLAT-11397) — the kebab's Edit Name is the
     heading's rename, and it says so in words. The kebab still reveals. */
  &:hover [data-chapter-menu] {
    opacity: 1;
  }

  /* PLAT-11064 — a portaled menu over a scrolling list is otherwise ambiguous
     about which heading it belongs to, so the heading holding it keeps its hover
     state (PLAT-11060 DS4, the trick InsertDivider uses to keep its + shown).
     Backtick-free on purpose: this sits inside a template literal. */
  ${props =>
    props.$hasOpenMenu &&
    `
    [data-chapter-menu] {
      opacity: 1;
    }
  `}

  /* The insertion line sits in the gap the drop would land in, the same line
     a Section row draws (PLAT-11053 DS4). */
  ${props =>
    props.$dropEdge &&
    `
    &::after {
      content: '';
      position: absolute;
      /* The card's own edges now that it is inset — the line marks where the
         drop lands, which is this card's boundary rather than the list's. */
      left: 0;
      right: 0;
      ${props.$dropEdge === 'above' ? 'top: 0;' : 'bottom: 0;'}
      height: 2px;
      border-radius: 1px;
      background-color: var(--primary);
    }
  `}
`;

/** Chevron over grip, the Section row's handle column at chapter scale. */
const HandleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

/** The chapter's drag affordance — the Section row's grip, same glyph. */
const Handle = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

/**
 * The title and its count: one heading, over two lines. No gap between them —
 * their own leading is the whole of the space, so the two read as one block
 * rather than as two rows that happen to be near each other.
 */
const Lines = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

/**
 * The collapse toggle (DS5). Points down when open and right when folded — the
 * direction the rows went.
 *
 * No tooltip on it: a chevron pointing down or right needs no caption, and one
 * here would cover the chapter title beside it. The `aria-label` stays, for
 * anyone not looking at the glyph.
 */
const Disclosure = styled.button<{ $collapsed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* The grip's width, so this column is the Section row's handle column. */
  width: 10px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transform: rotate(${props => (props.$collapsed ? '-90deg' : '0deg')});
  transition:
    transform var(--transition-fast),
    color var(--transition-fast);

  &:hover {
    color: var(--text-primary);
  }
`;

/**
 * Pulls the title's text onto the same left edge its count sits on. The rename
 * field insets its own text by its border + padding — the same 6px the Section
 * row's first line adds to sit under the name above it — so the field's box has
 * to start 6px to the left of where its text lands.
 */
const Title = styled.div<{ $isEditing?: boolean }>`
  display: flex;
  min-width: 0;
  margin-left: -6px;
  /* Only while the field is open, and then all of the line: an <input> has an
     intrinsic width of about 20 characters, so a box that sizes to its content
     hands the field that — a chapter title typed past it scrolls inside a box
     with the rest of the line sitting empty beside it (PLAT-11397). At rest the
     box goes back to hugging the text, which is what keeps "Contents changed"
     beside the title it belongs to rather than out at the rail. */
  ${props => props.$isEditing && 'flex: 1;'}
`;

/**
 * The card's own controls, stacked at its right edge: the kebab on the title's
 * line, the chevron under it on the summary's. The Section rows' rail one scale
 * up, so the two cards answer the pointer in the same place.
 */
const RightRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  flex-shrink: 0;
`;

/* The Section row's kebab, same shared trigger and same hover-reveal — a
   chapter's actions appear where a clip's do (`docs/design-system.md`,
   "Context / Action Menus"). */
const ChapterMenuButton = styled(KebabButton)`
  margin-left: 0;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background var(--transition-fast),
    color var(--transition-fast);

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

/** Its own line, in the title's column — no inset of its own to line them up. */
const Count = styled.div<{ $isError?: boolean }>`
  font-size: var(--text-xs);
  /* Its own scale's leading rather than the inherited body leading, which would
     add a couple of pixels of air under the title for nothing. */
  line-height: var(--leading-text-xs);
  color: ${props =>
    props.$isError ? 'var(--destructive)' : 'var(--text-muted)'};
  white-space: nowrap;
  /* The summary this line usually holds is short; a failure message is whatever
     the endpoint said, so it truncates rather than widening the heading. The
     full text stays available on the title attribute. */
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * PLAT-11064 DS2 — "this title is older than what it sits over". Deliberately
 * quiet: metadata weight, no colour of its own, no control. It says a thing is
 * available; the right-click menu is where it is available.
 */
const StaleHint = styled.span`
  flex-shrink: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-muted);
  font-style: italic;
  white-space: nowrap;
`;
